import { defineStore } from 'pinia';
import { ApiRequestConfig, IApiClientError } from '@cyber-range/cyber-range-api-client';
import { AuthenticationApiClient } from '@cyber-range/cyber-range-api-authentication-client';
import { EntitlementApiClient } from '@cyber-range/cyber-range-api-entitlement-client';
import { AgreementApiClient } from '@cyber-range/cyber-range-api-agreement-client';
import { BulkApiClient, IJobApiClient, JobApiClient } from '@cyber-range/cyber-range-api-job-client';
import Config from '../config';
import { useSessionStore } from './sessionStore';
import { useErrorStore } from './errorStore';
import { TrackJS } from 'trackjs';
import { ChallengeApiClient } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { SubmissionApiClient } from '@cyber-range/cyber-range-api-ctf-submission-client';
import { PlayerApiClient } from '@cyber-range/cyber-range-api-ctf-player-client';
import { TeamApiClient } from '@cyber-range/cyber-range-api-ctf-team-client';
import { CompetitionApiClient } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { FileApiClient } from '@cyber-range/cyber-range-api-file-client';
import { useAnalyticStore } from './analyticStore';

function customApiBackgroundErrorHandler(e:IApiClientError) 
{
    TrackJS.track(e);
    console.error(e);
    useAnalyticStore().trackError(e.message, e.statusCode);

    if(e.statusCode === 403 || e.statusCode === 422 || (e.statusCode === 400 && e.message === 'jwt expired'))
    {
        return useSessionStore().logout();
    }
}
function customApiErrorHandler(e:IApiClientError) 
{
    TrackJS.track(e);
    useErrorStore().setError(e);
}
function apiCallingHandler() 
{
    useApiClientStore().loadingBegin()
}
function apiCalledHandler() 
{
    useApiClientStore().loadingEnd()
}

function createApiClient<T>(T:{new(...args: any[]):T}, baseUrl:string, preConfigArgs: any[] = []): T
{
    let token = useSessionStore().token;

    let config = new ApiRequestConfig(
                            { "authorization": `Bearer ${token}`, 'User-Agent': undefined },
                            undefined,
                            customApiErrorHandler,
                            apiCallingHandler,
                            apiCalledHandler);

    return new T(baseUrl, ...preConfigArgs, config);
}

function createBackgroundApiClient<T>(T:{new(...args: any[]):T}, baseUrl:string, preConfigArgs: any[] = []): T
{
    let token = useSessionStore().token;

    let config = new ApiRequestConfig(
                            { "authorization": `Bearer ${token}`, 'User-Agent': undefined },
                            undefined,
                            customApiBackgroundErrorHandler,
                            undefined,
                            undefined);

    return new T(baseUrl, ...preConfigArgs, config);
}

function createBulkApiClient<T>(T:{new(...args: any[]):T}, baseUrl:string, jobClient: IJobApiClient): T
{
    return new T(undefined, undefined, new BulkApiClient(baseUrl, undefined, jobClient));
}

export const useApiClientStore = defineStore('apiClientStore', 
{
    state: () =>
    ({
        loading: 0,
        _authenticationApiClient: undefined,
        _entitlementApiClient: undefined,
        _jobApiClient: undefined,
        _backgroundJobApiClient: undefined,
        _competitionApiClient: undefined,
        _backgroundCompetitionApiClient: undefined,
        _playerApiClient: undefined,
        _backgroundPlayerApiClient: undefined,
        _teamApiClient: undefined,
        _backgroundTeamApiClient: undefined,
        _challengeApiClient: undefined,
        _backgroundChallengeApiClient: undefined,
        _submissionApiClient: undefined,
        _backgroundSubmissionApiClient: undefined,
        _fileApiClient: undefined,
        _backgroundFileApiClient: undefined,
        _agreementApiClient: undefined,
        _backgroundAgreementApiClient: undefined,
    }),
    getters: 
    {
        isLoading: (state) => state.loading > 0,
        authenticationApiClient: (state) => state._authenticationApiClient || createApiClient(AuthenticationApiClient, Config.AUTHENTICATION_API_BASE_URL),
        backgroundAuthenticationApiClient: (state) => createBackgroundApiClient(AuthenticationApiClient, Config.AUTHENTICATION_API_BASE_URL),
        entitlementApiClient: (state) => state._entitlementApiClient || createApiClient(EntitlementApiClient, Config.ENTITLEMENT_API_BASE_URL),
        backgroundEntitlementApiClient: (state) => createBackgroundApiClient(EntitlementApiClient, Config.ENTITLEMENT_API_BASE_URL),
        competitionApiClient: (state) => state._competitionApiClient || createApiClient(CompetitionApiClient, Config.COMPETITION_API_BASE_URL),
        backgroundCompetitionApiClient: (state) => state._backgroundCompetitionApiClient || createBackgroundApiClient(CompetitionApiClient, Config.COMPETITION_API_BASE_URL),
        playerApiClient: (state) => state._playerApiClient || createApiClient(PlayerApiClient, Config.PLAYER_API_BASE_URL),
        backgroundPlayerApiClient: (state) => state._backgroundPlayerApiClient || createBackgroundApiClient(PlayerApiClient, Config.PLAYER_API_BASE_URL),
        teamApiClient: (state) => state._teamApiClient || createApiClient(TeamApiClient, Config.TEAM_API_BASE_URL),
        backgroundTeamApiClient: (state) => state._backgroundTeamApiClient || createBackgroundApiClient(TeamApiClient, Config.TEAM_API_BASE_URL),
        challengeApiClient: (state) => state._challengeApiClient || createApiClient(ChallengeApiClient, Config.CHALLENGE_API_BASE_URL, [{}]), // Don't need fileApiClient in this project.
        backgroundChallengeApiClient: (state) => state._backgroundChallengeApiClient || createBackgroundApiClient(ChallengeApiClient, Config.CHALLENGE_API_BASE_URL, [{}]),// Don't need fileApiClient in this project.
        submissionApiClient: (state) => state._submissionApiClient || createApiClient(SubmissionApiClient, Config.SUBMISSION_API_BASE_URL),
        backgroundSubmissionApiClient: (state) => state._backgroundSubmissionApiClient || createBackgroundApiClient(SubmissionApiClient, Config.SUBMISSION_API_BASE_URL),
        fileApiClient: (state) => state._fileApiClient || createApiClient(FileApiClient, Config.FILE_API_BASE_URL),
        backgroundFileApiClient: (state) => state._backgroundFileApiClient || createBackgroundApiClient(FileApiClient, Config.FILE_API_BASE_URL),
        jobApiClient: (state) => state._jobApiClient || createApiClient(JobApiClient, Config.JOB_API_BASE_URL),
        backgroundJobApiClient: (state) => state._backgroundJobApiClient || createBackgroundApiClient(JobApiClient, Config.JOB_API_BASE_URL),
        agreementApiClient: (state) => state._agreementApiClient || createApiClient(AgreementApiClient, Config.AGREEMENT_API_BASE_URL),
        backgroundAgreementApiClient: (state) => state._backgroundAgreementApiClient || createBackgroundApiClient(AgreementApiClient, Config.AGREEMENT_API_BASE_URL),
    },
    actions:
    {
        loadingBegin(): number
        {
            return ++this.loading;
        },
        loadingEnd(): number
        {
            return --this.loading;
        }
    }
});
